import { useState } from 'react';
import Link from 'next/link';
import key from 'weak-key';
import { ReactSVG } from 'react-svg';
import { has, getIcon, getTaxonomyTerms } from '~/utils/utils';
import SiteSettings from '~/siteSettings';
import VideoPreview from '../videoPreview/videoPreview';

/**
 * Featured Block
 * @category Functional Component
 * @summary Displays a static block.
 * @prop {Object} items - The block items.
 * @prop {Object} title - The block title.
 * @prop {bool} seeMore - Should we paginate results?.
 * @prop {bool} seeMoreAt - When should we paginate?.
 * @prop {bool} ajaxTerm - Only supports tax terms, will use ajax based pagination instead of css.
 * @prop {bool} itemsPerPage - To be paired with ajax term, how many items per page.
 * @prop {bool} itemCount - To be paired with ajax term, what is the total result count?
 * @return {Node} React node containing the featured block.
 */

const BlockFeatured = props => {
  const { items, title, seeMore, seeMoreAt, ajaxTerm, itemsPerPage, itemCount } = props;
  const [isVisible, setIsVisible] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const featured = items[0];
  const [offset, setOffset] = useState(items.length - itemsPerPage);
  const [notFeatured, setNotFeatured] = useState(items.filter((item, i) => i !== 0 && item !== null));

  const getMore = async () => {
    const incrementOffset = offset + itemsPerPage;

    setIsLoading(true);

    const taxonomy = await getTaxonomyTerms({
      machine_name: 'latest_content_by_term',
      display: 'master',
      itemsPerPage,
      offset: incrementOffset,
      viewArgs: [
        'video,article,gallery,recipe',
        ajaxTerm.toString()
      ],
      target_id: parseInt(ajaxTerm, 10)
    });

    setIsLoading(false);
    setOffset(incrementOffset);
    setNotFeatured([...notFeatured, ...taxonomy.viewQuery.resultRow])
  }

  const getClass = (i) => {
    if(seeMoreAt) {
      return i >= seeMoreAt && !isVisible && seeMore ? 'block-featured__item visuallyhidden' : 'block-featured__item';
    }

    return 'block-featured__item';
  }

  return (
    <div className='constrain'>
      <div className='block-featured'>
        <h2 className='block-featured__heading'>{ title }</h2>
        <div className='block-featured__feature'>
          <div className='block-featured__feature-image'>
            <Link legacyBehavior href={ featured.path }>
              <a aria-label={`Visit ${ featured.title }`}>
                { featured.field_secondary_image &&
                  featured.field_secondary_image.field_image.field_image.sm &&
                  featured.field_secondary_image.field_image.field_image.lg &&
                  <picture>
                    <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ featured.field_secondary_image.field_image.field_image.sm.url } />
                    <img
                      src={ featured.field_secondary_image.field_image.field_image.lg.url }
                      alt={ featured.field_secondary_image.field_image?.alt }
                      width={ featured.field_secondary_image.field_image.field_image.lg.width }
                      height={ featured.field_secondary_image.field_image.field_image.lg.height }
                      className='card__img'
                      loading='lazy'
                    />
                  </picture>
                }
                { featured.field_image.field_image &&
                  !featured.field_secondary_image &&
                  featured.field_image.field_image.sm &&
                  featured.field_image.field_image.lg &&
                  <picture>
                    <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ featured.field_image.field_image.sm.url } />
                    <img
                      src={ featured.field_image.field_image.lg.url }
                      alt={ featured.field_image?.alt }
                      width={ featured.field_image.field_image.lg.width }
                      height={ featured.field_image.field_image.lg.height }
                      className='card__img'
                      loading='lazy'
                    />
                  </picture>
                }
              </a>
            </Link>
          </div>
          <div className='block-featured__feature-body'>
            <div>
              <div className='card__type-icon card__type-icon--dark'>
                <ReactSVG src={ getIcon(featured.__typename) } />
              </div>
              { featured.title &&
                <Link legacyBehavior href={ featured.path }>
                  <a aria-label={`Visit ${ featured.title }`} className='hidden-link'>
                    <h2>{ featured.title }</h2>
                  </a>
                </Link>
              }
              {/* Category */}
              { featured.field_category && featured.field_category.path &&
                <div className='block-featured__category'>
                  <Link legacyBehavior href={ featured.field_category.path }>
                    { featured.field_category.name }
                  </Link>
                </div>
              }
              {/* Category (if no url) */}
              { featured.field_category && featured.field_category.path === undefined &&
                <div className='block-featured__category'>
                  { featured.field_category.name }
                </div>
              }
              { featured.field_subhead &&
                <p>{ featured.field_subhead }</p>
              }
            </div>
          </div>
        </div>
        <div className='block-featured__grid'>
          {notFeatured.map((item, i) => (
            <div className='card' key={`block-featured-item-${key(item)}`}>
              <Link legacyBehavior href={ item.path }>
                <a
                  aria-label={`Visit ${ item.title }`}
                  className={ getClass(i) }
                >
                  { item.__typename === 'video' && has(item, 'field_image.sm') &&
                    <VideoPreview item={ item }>
                      <picture>
                        <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ item.field_image.field_image.sm.url } />
                        <img
                          src={ item.field_image.field_image.lg.url }
                          alt={ item.field_image?.alt }
                          width={ item.field_image.field_image.lg.width }
                          height={ item.field_image.field_image.lg.height }
                          className='card__img'
                          loading='lazy'
                        />
                      </picture>
                    </VideoPreview>
                  }
                  { item.__typename !== 'video' && item.field_image.field_image !== undefined &&
                    <picture>
                      <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ item.field_image.field_image.sm.url } />
                      <img
                        src={ item.field_image.field_image.lg.url }
                        alt={ item.field_image?.alt }
                        width={ item.field_image.field_image.lg.width }
                        height={ item.field_image.field_image.lg.height }
                        className='card__img'
                        loading='lazy'
                      />
                    </picture>
                  }
                  <h3>
                    <div className='card__type-icon'>
                      <ReactSVG src={ getIcon(item.__typename) } />
                    </div>
                    { item.title }
                  </h3>
                </a>
              </Link>
            </div>
          ))}
        </div>
        { !isVisible && seeMore && notFeatured.length > 2 &&
          <button
            aria-label='See More'
            type='button'
            onClick={() => setIsVisible(true)}
            className='btn__cta'
          >
            <span>
              { isLoading && <img src='/img/loader.gif' alt='Loading' /> } see more
            </span>
          </button>
        }
        { (itemCount - 1 !== notFeatured.length && ajaxTerm) &&
          <button
            aria-label='See More'
            type='button'
            onClick={() => getMore()}
            className='btn__cta'
          >
            <span>
              { isLoading && <img src='/img/loader.gif' alt='Loading' /> } see more
            </span>
          </button>
        }
      </div>
    </div>
  );
}

export default BlockFeatured;
